import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import Joi from "joi-browser";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Checkbox from "./common/Checkbox";
import Input from "./common/Input";
import TrueInput from "./common/TrueInput";
import Select from "./common/Select";
import {
  getAvailableEmployees,
  getAvailableEmployeesByState,
  getAvailableExecutives,
  getCurrentlyOnCallv2,
  getHistoricalOnCall,
  saveExecutiveOnCall,
  saveInPersonOnCallv2,
  saveOnCallAssignment,
  sendEmail,
} from "./services/soar";

function OnCall() {
  const [location, setLocation] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [backTo, setBackTo] = useState("");
  const [onCall, setOnCall] = useState([]);
  const [executiveOnCall, setExecutiveOnCall] = useState({});
  const [loadStatus, setLoadStatus] = useState({});
  const [historicalOnCall, setHistoricalOnCall] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isHistoricalOpen, setIsHistoricalOpen] = useState(false);
  const [data, setData] = useState({});
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [stateEmployeeOptions, setStateEmployeeOptions] = useState([]);
  const [executiveDirectorOptions, setExecutiveDirectorOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    SelectedCompany: Joi.string().required(),
    SelectedDivision: Joi.string().required(),
    LocationIndicator: Joi.string().required(),
    OutOfRegion: Joi.boolean(),
    SelectedSpecialist: Joi.string().required(),
    SpecialistPhone: Joi.string().required(),
    SelectedManager: Joi.string().required(),
    ManagerPhone: Joi.string().required(),
    InPerson: Joi.when("SelectedCompany", {
      is: moment().isAfter(moment("2025-04-04"))
        ? Joi.any().valid("SAFY of Kentucky", "SAFY of Alabama")
        : "SAFY of Kentucky",
      then: Joi.array()
        .items(
          Joi.object({
            ReceiverId: Joi.string().required(),
            ReceiverName: Joi.string().required(),
            ReceiverPhone: Joi.string().required(),
          })
        )
        .min(1),
      otherwise: Joi.when("SelectedDivision", {
        is: "America",
        then: Joi.array()
          .items(
            Joi.object({
              ReceiverId: Joi.string().required(),
              ReceiverName: Joi.string().required(),
              ReceiverPhone: Joi.string().required(),
            })
          )
          .min(1),
        otherwise: Joi.when("SelectedDivision", {
          is: "IFCS",
          then: Joi.array()
            .items(
              Joi.object({
                ReceiverId: Joi.string().required(),
                ReceiverName: Joi.string().required(),
                ReceiverPhone: Joi.string().required(),
              })
            )
            .min(1),
          otherwise: Joi.any(),
        }),
      }),
    }),
    PreviousSpecialist: Joi.string().allow(""),
    PreviousSpecialistName: Joi.string().allow(""),
    PreviousManager: Joi.string().allow(""),
    PreviousManagerName: Joi.string().allow(""),
    PreviousInPerson: Joi.array().items(
      Joi.object({
        ReceiverId: Joi.string().required(),
        ReceiverName: Joi.string().required(),
        ReceiverPhone: Joi.string().required(),
      })
    ),
  };

  //Route Access Protection
  useEffect(() => {
    if (
      !safyInfo?.user?.modules?.includes?.("Admin") &&
      process.env.REACT_APP_ENVIRONMENT !== "Production"
    ) {
      navigate("/");
    }

    if (!safyInfo.user.safyInfo?.BusinessUnitIdName) {
      navigate("/");
    }

    var tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );

    tooltipTriggerList?.map?.(function (tooltipTriggerEl) {
      return new bootstrap.Tooltip(tooltipTriggerEl);
    });
  });

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const loadExecutiveOptions = async () => {
      const response = await getAvailableExecutives(signal);
      const result = JSON.parse(response).OnCall.AvailableExecutives;

      if (!signal.aborted) {
        setExecutiveDirectorOptions(
          result
            .sort((a, b) => {
              if (a.listtext > b.listtext) return 1;
              if (a.listtext < b.listtext) return -1;
              return 0;
            })
            .map((employee) => {
              const employeeInfo = employee.listvalue.split("|");

              return {
                _id: employeeInfo[0],
                name: employee.listtext,
                phone: employeeInfo[1],
              };
            })
        );
      }
    };

    loadExecutiveOptions();
    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, []);

  const getOnCall = useCallback(
    async (businessUnit) => {
      const toastId = toast.loading("Loading On Call...", {
        type: toast.TYPE.INFO,
      });

      try {
        const response = await getCurrentlyOnCallv2();

        if (!isMountedRef.current) {
          return;
        }

        if (!response) {
          toast.update(toastId, {
            render:
              "No response, please refresh to try againkm,  , and if the problem persists, contact SOAR Support",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          return;
        }

        toast.update(toastId, {
          render: "On Call loaded successfully.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        const result = JSON.parse(response).OnCall.Current;
        let filteredToState = result;

        if (
          businessUnit.toLowerCase() !== "ccplive" &&
          safyInfo?.user?.safyInfo?.isExecutiveOnCall !== "1"
        ) {
          filteredToState = result.filter(
            (item) => item.Region === businessUnit
          );
        }

        filteredToState.forEach(
          (oc) => (oc.InPerson = JSON.parse(oc.InPerson || "[]"))
        );
        setOnCall(filteredToState);

        const options = filteredToState.map((item) => ({
          _id: item.LocationIndicator,
          name: item.LocationIndicator,
        }));

        setLocationOptions(options);

        setExecutiveOnCall({
          _id: result[0].ExecutiveId,
          name: result[0].ExecutiveName,
          phone: result[0].ExecutivePhone,
        });
      } catch {
        toast.update(toastId, {
          render: "An error occurred while loading On Call.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      }
    },
    [safyInfo?.user?.safyInfo?.isExecutiveOnCall]
  );

  useEffect(() => {
    if (
      loadStatus.loaded ||
      loadStatus.loading ||
      !safyInfo?.user?.safyInfo?.BusinessUnitIdName
    ) {
      return;
    }

    const loadOnCall = async () => {
      setLoadStatus({ loading: true });
      await getOnCall(safyInfo.user.safyInfo.BusinessUnitIdName);

      if (!isMountedRef.current) {
        return;
      }

      setLoadStatus({ loaded: true });
    };

    loadOnCall();
  }, [safyInfo?.user?.safyInfo?.BusinessUnitIdName, loadStatus, getOnCall]);

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const value = input.type === "checkbox" ? input.checked : input.value;
    const errorMessage = validateProperty({ name: input.name, value: value });
    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    const obj = { ...data };
    obj[input.name] = value;
    setData(obj);
    setErrors(errs);
    return obj;
  };

  const handleSelectedSpecialistChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });
    let employee = employeeOptions.filter(
      (option) => option._id === input.value
    )[0];

    if (!employee) {
      obj.OutOfRegion = true;
      employee = stateEmployeeOptions.filter(
        (option) => option._id === input.value
      )[0];
      setData(obj);
    }

    if (employee?.phone) {
      obj.SpecialistPhone = employee.phone;
      setData(obj);

      const errs = { ...errors };
      delete errs.SpecialistPhone;
      setErrors(errs);
    } else {
      obj.SpecialistPhone = "";
    }
  };

  const handleSelectedManagerChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });
    const employee = stateEmployeeOptions.filter(
      (option) => option._id === input.value
    )[0];

    if (employee?.phone) {
      obj.ManagerPhone = employee.phone;
      setData(obj);

      const errs = { ...errors };
      delete errs.ManagerPhone;
      setErrors(errs);
    } else {
      obj.ManagerPhone = "";
    }
  };

  const handleSelectedInPersonChange = (
    { currentTarget: input },
    index = 0
  ) => {
    let employee = employeeOptions.find((option) => option._id === input.value);

    if (!employee) {
      employee = stateEmployeeOptions.find(
        (option) => option._id === input.value
      );
    }

    setData((d) => {
      const obj = { ...d };
      const inPerson = obj.InPerson[index] ? obj.InPerson[index] : {};
      inPerson.ReceiverId = employee._id;
      inPerson.ReceiverName = employee.name;

      if (employee?.phone) {
        inPerson.ReceiverPhone = employee.phone;
        const errs = { ...errors };
        delete errs.InPerson;
        setErrors(errs);
      } else {
        obj.InPerson[index].ReceiverPhone = "";
      }

      return obj;
    });
  };

  const handleInPersonPhoneChange = ({ currentTarget: input }, index = 0) => {
    if (!/^\d+$/.test(input.value)) {
      const errs = { ...errors };
      errs.InPerson = "Numbers only, no dashes";
      setErrors(errs);
      return;
    }

    setData((d) => {
      const obj = { ...d };
      const inPerson = obj.InPerson[index] ? obj.InPerson[index] : {};
      inPerson.ReceiverPhone = input.value;
      obj.InPerson[index] = inPerson;
      return obj;
    });

    if (input.value.length !== 10) {
      const errs = { ...errors };
      errs.InPerson = "Phone number must be 10 digits";
      setErrors(errs);
      return;
    }

    if (errors.InPerson !== undefined) {
      const errs = { ...errors };
      delete errs.InPerson;
      setErrors(errs);
    }
  };

  const handleViewHistory = async () => {
    const errs = { ...errors };
    if (location === "") errs.Location = "'Location' is required";
    if (backTo === "") errs.BackTo = "'Back To' is required";

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsHistoricalOpen(true);
    const backToString = moment.utc(backTo).format("MM-DD-YYYY");
    const response = await getHistoricalOnCall(location, backToString);

    if (!isMountedRef.current || !response) {
      return;
    }

    const result = JSON.parse(response).OnCall.History;
    setHistoricalOnCall(result);
  };

  const handleHideModal = () => {
    setData({});
    setIsOpen(false);
    setIsHistoricalOpen(false);
    setErrors({});
  };

  const handleChangeOnCall = async (item) => {
    const obj = {
      SelectedCompany: item.Region,
      SelectedDivision: item.Division,
      LocationIndicator: item.LocationIndicator,
      SelectedSpecialist: item.PrimaryReceiverId,
      SpecialistPhone: item.PrimaryReceiverPhone,
      SelectedManager: item.SecondaryReceiverId,
      ManagerPhone: item.SecondaryReceiverPhone,
      InPerson: item.InPerson,
      PreviousManager: item.SecondaryReceiverId,
      PreviousManagerName: item.SecondaryReceiverName,
      PreviousSpecialist: item.PrimaryReceiverId,
      PreviousSpecialistName: item.PrimaryReceiverName,
      PreviousInPerson: item.InPerson,
    };

    if (obj.SelectedDivision === "IFCS") {
      while (obj.InPerson.length < 5) {
        obj.InPerson.push({
          ReceiverId: "",
          ReceiverName: "",
          ReceiverPhone: "",
        });
      }
    }

    setData(obj);

    const eOptions = [
      { _id: obj.PreviousSpecialist, name: obj.PreviousSpecialistName },
    ];
    const sOptions = [
      { _id: obj.PreviousManager, name: obj.PreviousManagerName },
    ];

    if (obj.PreviousInPerson?.length > 0) {
      const pOptions = obj.PreviousInPerson.map((person) => ({
        _id: person.ReceiverId,
        name: person.ReceiverName,
        phone: person.ReceiverPhone,
      }));
      setEmployeeOptions(eOptions.concat(pOptions));
      setStateEmployeeOptions(sOptions.concat(pOptions));
    } else {
      setEmployeeOptions(eOptions);
      setStateEmployeeOptions(sOptions);
    }

    setIsOpen(true);

    const splitIndicator = obj.LocationIndicator.split("_");
    const actualIndicator = `${splitIndicator[0]}_${splitIndicator[1]}`;

    if (!obj.SelectedCompany.includes("Indiana")) {
      const response = await getAvailableEmployees(actualIndicator);
      if (!isMountedRef.current || !response) {
        return;
      }

      const result = JSON.parse(response).OnCall.AvailableEmployees;
      console.log("EmployeeResults: ", result);
      let employeeOptionExists = false;

      const options = result
        .sort((a, b) => {
          if (a.listtext > b.listtext) return 1;
          if (a.listtext < b.listtext) return -1;
          return 0;
        })
        .map((employee) => {
          const employeeInfo = employee.listvalue.split("|");

          if (employeeInfo[0] === obj.PreviousSpecialist) {
            employeeOptionExists = true;
          }

          return {
            _id: employeeInfo[0],
            name: employee.listtext,
            phone: employeeInfo[1],
          };
        });
      setEmployeeOptions(options);

      if (options.length === 0 || !employeeOptionExists) {
        obj.OutOfRegion = true;
      }

      if (actualIndicator === "OH_America") setStateEmployeeOptions(options);
    }

    if (actualIndicator !== "OH_America") {
      const response = await getAvailableEmployeesByState(obj.SelectedCompany);

      if (!isMountedRef.current || !response) {
        return;
      }

      const result = JSON.parse(response).OnCall.AvailableEmployees;
      console.log("EmployeeByStateResults: ", result);
      const options = result
        .sort((a, b) => {
          if (a.listtext > b.listtext) return 1;
          if (a.listtext < b.listtext) return -1;
          return 0;
        })
        .map((employee) => {
          const employeeInfo = employee.listvalue.split("|");
          return {
            _id: employeeInfo[0],
            name: employee.listtext,
            phone: employeeInfo[1],
          };
        });
      setStateEmployeeOptions(options);

      if (obj.SelectedCompany.includes("Indiana")) setEmployeeOptions(options);
    }
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);

    let validationErrors = {};
    if (error)
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;

    if (Object.keys(validationErrors).length === 0) return null;

    setErrors(validationErrors);
    return validationErrors;
  };

  const validateProperty = ({ name, value }) => {
    console.log("Validate: ", name, value);
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema, { convert: true });
    return error ? error.details[0].message : null;
  };

  const handleSave = async () => {
    if (!validate()) {
      handleHideModal();
      const dataObj = { ...data };

      let specialistOptions = employeeOptions;

      if (dataObj.OutOfRegion) {
        specialistOptions = stateEmployeeOptions;
      }

      const specialist = specialistOptions.filter(
        (option) => option._id === dataObj.SelectedSpecialist
      )[0];
      const manager = stateEmployeeOptions.filter(
        (option) => option._id === dataObj.SelectedManager
      )[0];
      const callLog = {
        LocationIndicator: dataObj.LocationIndicator,
        PrimaryReceiverId: dataObj.SelectedSpecialist,
        PrimaryReceiverName: specialist.name,
        PrimaryReceiverPhone: dataObj.SpecialistPhone,
        SecondaryReceiverId: dataObj.SelectedManager,
        SecondaryReceiverName: manager.name,
        SecondaryReceiverPhone: dataObj.ManagerPhone,
        CreatedBy: safyInfo.user.domainName.replace("safy\\", ""),
      };
      const toastId = toast.loading("Updating On Call assignment...", {
        type: toast.TYPE.INFO,
      });

      const saveOnCall = async () => {
        try {
          await saveOnCallAssignment(callLog);
          handleSendEmail();
          toast.update(toastId, {
            render: "On Call assignment has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        } catch {
          toast.update(toastId, {
            render: "Error updating On Call assignment.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        }

        if (!isMountedRef.current) {
          return;
        }

        setData({});
        getOnCall(safyInfo.user.safyInfo.BusinessUnitIdName);
      };

      saveOnCall();

      if (
        dataObj.SelectedCompany.includes("Kentucky") ||
        (moment().isAfter(moment("2025-04-04")) &&
          dataObj.SelectedCompany.includes("Alabama")) ||
        dataObj.SelectedDivision === "America" ||
        dataObj.SelectedDivision === "IFCS"
      ) {
        const saveInPerson = async () => {
          const obj = {
            LocationIndicator: dataObj.LocationIndicator,
            AssignedArray: JSON.stringify(dataObj.InPerson),
            CreatedBy: safyInfo.user.domainName.replace("safy\\", ""),
          };

          saveInPersonOnCallv2(obj);
        };

        saveInPerson();
      }
    }
  };

  const handleSendEmail = () => {
    const toAddresses = [];
    toAddresses.push(`${data.SelectedSpecialist}@safy.org`);
    toAddresses.push(`${data.SelectedManager}@safy.org`);

    data.InPerson?.forEach?.((person) => {
      if (!toAddresses.includes(`${person.ReceiverId}@safy.org`)) {
        toAddresses.push(`${person.ReceiverId}@safy.org`);
      }
    });

    if (
      data.PreviousSpecialist &&
      !toAddresses.includes(`${data.PreviousSpecialist}@safy.org`)
    ) {
      toAddresses.push(`${data.PreviousSpecialist}@safy.org`);
    }

    if (
      data.PreviousManager &&
      !toAddresses.includes(`${data.PreviousManager}@safy.org`)
    ) {
      toAddresses.push(`${data.PreviousManager}@safy.org`);
    }

    data.PreviousInPerson?.forEach?.((person) => {
      if (!toAddresses.includes(`${person.ReceiverId}@safy.org`)) {
        toAddresses.push(`${person.ReceiverId}@safy.org`);
      }
    });

    let specialistOptions = employeeOptions;

    if (data.OutOfRegion) {
      specialistOptions = stateEmployeeOptions;
    }

    const specialist = specialistOptions.filter(
      (option) => option._id === data.SelectedSpecialist
    )[0];
    const manager = stateEmployeeOptions.filter(
      (option) => option._id === data.SelectedManager
    )[0];

    const getInPersonMarkup = (inPersonArray) => {
      if (!inPersonArray || inPersonArray.length === 0) {
        return "";
      }

      if (
        data.SelectedCompany.includes("Kentucky") ||
        data.SelectedCompany.includes("Alabama") ||
        data.SelectedDivision === "America"
      ) {
        const inPersonOption = specialistOptions.find(
          (option) => option._id === data.InPerson[0].ReceiverId
        );

        return inPersonOption
          ? `<li>In Person: ${inPersonOption.name} - (${data.InPerson[0].ReceiverPhone})</li>`
          : "";
      } else if (data.SelectedDivision === "IFCS") {
        const divisions = [
          "Fort Wayne",
          "Indianapolis",
          "Merrillville",
          "Mishawaka",
          "Southern Indiana",
        ];

        return inPersonArray
          .map((person, index) => {
            const option = specialistOptions.find(
              (opt) => opt._id === person.ReceiverId
            );
            return option
              ? `<li>${divisions[index]} In Person: ${option.name} - (${person.ReceiverPhone})</li>`
              : "";
          })
          .join("");
      }
    };

    let body = `<p>The on-call employees have changed for your location (${
      data.LocationIndicator
    }).</p><p><strong>To:</strong></p><ul><li>Primary: ${specialist.name} - (${
      data.SpecialistPhone
    })</li><li>Secondary: ${manager.name} - (${
      data.ManagerPhone
    })</li>${getInPersonMarkup(data.InPerson)}</ul>`;
    if (data.PreviousSpecialistName !== "" && data.PreviousManagerName !== "")
      body += `<p><strong>From:</strong></p><ul><li>Primary: ${
        data.PreviousSpecialistName
      }</li><li>Secondary: ${data.PreviousManagerName}</li>${getInPersonMarkup(
        data.PreviousInPerson
      )}</ul>`;

    body += `<br/><br/>Changed by: ${safyInfo.user.displayName}`;

    sendEmail(
      toAddresses,
      "",
      "On Call Person Changed",
      body,
      safyInfo.user.email
    ).then((response) => {
      const result = JSON.parse(response);

      if (result.SystemResponse[0].Code === "Success") {
        toast.success(`On Call change notification email sent`);
      } else {
        toast.error(
          `Error in sending email: ${result.SystemResponse[0].Message}`
        );
      }
    });
  };

  const formatPhoneNumber = (phoneNumberString) => {
    var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return null;
  };

  const handleSaveExecutiveOnCall = () => {
    if (!executiveOnCall?._id) {
      setErrors((e) => {
        return { ...e, ExecutiveOnCall: "'Executive Director' is required." };
      });
    } else {
      const obj = {
        ExecutiveId: executiveOnCall._id,
        ExecutiveName: executiveOnCall.name,
        ExecutivePhone: executiveOnCall.phone,
        CreatedBy: safyInfo.user.safyInfo.ADUser,
      };

      saveExecutiveOnCall(obj);
    }
  };

  const handleExecutiveOnCallChange = ({ currentTarget: input }) => {
    const selected = executiveDirectorOptions.filter(
      (option) => option._id === input.value
    )[0];
    setExecutiveOnCall(selected || {});
  };

  const checkEDOnCall = (directorOnCall, currentOptions) => {
    if (
      currentOptions.map((option) => option._id).indexOf(directorOnCall) !== -1
    )
      return currentOptions;

    return [executiveOnCall, ...currentOptions];
  };

  return (
    <>
      <h2 className="text-center">Divisional On Call Management</h2>
      <h5 className="text-safy1-1">On-Call History Report</h5>
      <div className="container">
        <div className="row">
          <div className="col">
            <Select
              name="Location"
              label="Location:"
              options={locationOptions}
              onChange={(e) => {
                if (errors.Location !== undefined) {
                  const errs = { ...errors };
                  delete errs.Location;
                  setErrors(errs);
                }
                setLocation(e.currentTarget.value);
              }}
              error={errors.Location}
            />
          </div>
          <div className="col">
            <Input
              name="BackTo"
              label="Back to:"
              type="date"
              onChange={(e) => {
                if (errors.BackTo !== undefined) {
                  const errs = { ...errors };
                  delete errs.BackTo;
                  setErrors(errs);
                }
                setBackTo(e.currentTarget.value);
              }}
              error={errors.BackTo}
            />
          </div>
          {safyInfo?.user?.modules?.indexOf("On Call") !== -1 ? (
            <div className="col">
              <div className="row">
                <div className="col">
                  <Select
                    name="ExecutiveOnCall"
                    label="Executive Director On Call:"
                    value={executiveOnCall._id}
                    options={checkEDOnCall(
                      executiveOnCall._id,
                      executiveDirectorOptions
                    )}
                    onChange={handleExecutiveOnCallChange}
                    error={errors.ExecutiveOnCall}
                  />
                </div>
                <div className="col-3" style={{ marginTop: "30px" }}>
                  <div
                    className="btn btn-primary"
                    onClick={handleSaveExecutiveOnCall}
                  >
                    Save
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="clearfix">
        <div className="h5 float-start">Currently On Call</div>
        <div className="float-end">
          <button className="btn btn-link" onClick={handleViewHistory}>
            View History
          </button>
        </div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Region</th>
            <th>Division</th>
            <th colSpan="2"></th>
            <th>Specialist</th>
            <th>Manager</th>
          </tr>
        </thead>
        <tbody>
          {onCall.map((item) => (
            <tr key={item.Region + item.Division}>
              <td>
                <Link
                  title="Call Log"
                  to={`/callhistory/${item.LocationIndicator}`}
                >
                  <i className="fa fa-phone text-safy-success pe-2" />
                </Link>
                {item.Region}
              </td>
              <td
                data-bs-html="true"
                data-bs-toggle="tooltip"
                data-bs-title={
                  item.DivisionHover &&
                  `${JSON.parse(item.DivisionHover)
                    .map((dh) => {
                      return `${dh.Type}: ${formatPhoneNumber(dh.Phone)}`;
                    })
                    .join("<br/>")}`
                }
                data-bs-custom-class="custom-tooltip"
                data-bs-placement="left"
              >
                {item.Division}
              </td>
              <td>
                {safyInfo?.user?.safyInfo?.isExecutiveOnCall === "1" ? null : (
                  <button
                    className="btn btn-link"
                    onClick={() => {
                      handleChangeOnCall(item);
                    }}
                  >
                    Change
                  </button>
                )}
              </td>
              <td>
                {item.InPerson?.length > 0 ? (
                  <i
                    className="fa-solid fa-circle-info fa-xl text-info"
                    data-bs-html="true"
                    data-bs-toggle="tooltip"
                    data-bs-title={`${item.InPerson.map(
                      (i) =>
                        `${i.ReceiverName} - ${formatPhoneNumber(
                          i.ReceiverPhone
                        )}`
                    ).join("<br/>")}`}
                    data-bs-custom-class="custom-tooltip"
                    data-bs-placement="auto"
                  />
                ) : null}
              </td>
              <td>
                <span
                  data-bs-toggle="tooltip"
                  data-bs-title={formatPhoneNumber(item.PrimaryReceiverPhone)}
                  data-bs-custom-class="custom-tooltip"
                >
                  {item.Specialist}
                </span>
              </td>
              <td>
                <span
                  data-bs-toggle="tooltip"
                  data-bs-title={formatPhoneNumber(item.SecondaryReceiverPhone)}
                  data-bs-custom-class="custom-tooltip"
                >
                  {item.Manager}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isHistoricalOpen ? (
        <Modal show={isHistoricalOpen} animation={false} size="lg">
          {/* <Modal.Header>
          <Modal.Title>Location History</Modal.Title>
        </Modal.Header> */}
          <Modal.Body>
            <h5>Location History</h5>
            <div className="border px-4 py-2 m-2 bg-safy3-1">
              <div className="row border-bottom mb-2">
                <div className="col">Specialist</div>
                <div className="col">Phone</div>
                <div className="col">Manager</div>
                <div className="col">Phone</div>
              </div>
              <div className="row border-bottom">
                <div className="col">Started by</div>
                <div className="col">Started on</div>
                <div className="col">Ended on</div>
                <div className="col">Hours on</div>
              </div>
            </div>
            {historicalOnCall.map((item, index) => (
              <React.Fragment key={`Historical.${index}`}>
                <div className="p-2 row border-bottom">
                  <div className="col">{item.PrimaryReceiverName}</div>
                  <div className="col">
                    {item.PrimaryReceiverPhone.length === 10
                      ? `(${item.PrimaryReceiverPhone.substring(
                          0,
                          3
                        )})-${item.PrimaryReceiverPhone.substring(
                          3,
                          6
                        )}-${item.PrimaryReceiverPhone.substring(6, 10)} `
                      : item.PrimaryReceiverPhone}
                  </div>
                  <div className="col">{item.SecondaryReceiverName}</div>
                  <div className="col">
                    {item.SecondaryReceiverPhone.length === 10
                      ? `(${item.SecondaryReceiverPhone.substring(
                          0,
                          3
                        )})-${item.SecondaryReceiverPhone.substring(
                          3,
                          6
                        )}-${item.SecondaryReceiverPhone.substring(6, 10)} `
                      : item.SecondaryReceiverPhone}
                  </div>
                </div>
                <div className="p-2 mb-2 text-white bg-safy3-1 row">
                  <div className="col">{item.CreatedBy}</div>
                  <div className="col">
                    {moment.utc(item.CreatedOn).format("MM/DD/YYYY")}
                  </div>
                  <div className="col">
                    {item.LogEnded !== "1900-01-01T00:00:00" &&
                      moment.utc(item.LogEnded).format("MM/DD/YYYY")}
                  </div>
                  <div className="col">{item.hrs}</div>
                </div>
              </React.Fragment>
            ))}
            <button className="btn btn-primary" onClick={handleHideModal}>
              Close
            </button>
          </Modal.Body>
        </Modal>
      ) : null}
      {isOpen ? (
        <Modal
          show={isOpen}
          animation={false}
          size={`${data.SelectedDivision === "IFCS" ? "xl" : ""}`}
        >
          <Modal.Header>
            <Modal.Title>Change On Call Assignment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5>{`${data.SelectedCompany}-${data.SelectedDivision}`}</h5>
            {data.SelectedDivision !== "IFCS" ? (
              <>
                <div className="border rounded p-2 mt-3 mb-4">
                  {data.SelectedDivision !== "America" && (
                    <Checkbox
                      name="OutOfRegion"
                      label="Specialist is outside of selected Division"
                      checked={data.OutOfRegion}
                      onChange={handleChange}
                      divClassName="ms-1"
                    />
                  )}
                  <Select
                    name="SelectedSpecialist"
                    label="Specialist"
                    value={data.SelectedSpecialist}
                    options={
                      data.OutOfRegion ? stateEmployeeOptions : employeeOptions
                    }
                    onChange={handleSelectedSpecialistChange}
                    error={errors.SelectedSpecialist}
                  />
                  <TrueInput
                    name="SpecialistPhone"
                    label="Phone"
                    value={data.SpecialistPhone}
                    onChange={(e) => {
                      if (!/^\d+$/.test(e.currentTarget.value)) {
                        const errs = { ...errors };
                        errs.SpecialistPhone = "Numbers only, no dashes";
                        setErrors(errs);
                        return;
                      }

                      if (e.currentTarget.value.length !== 10) {
                        const errs = { ...errors };
                        errs.SpecialistPhone = "Phone number must be 10 digits";
                        setErrors(errs);
                      }

                      if (errors.SpecialistPhone !== undefined) {
                        const errs = { ...errors };
                        delete errs.SpecialistPhone;
                        setErrors(errs);
                      }

                      const obj = { ...data };
                      obj.SpecialistPhone = e.currentTarget.value;
                      setData(obj);
                    }}
                    error={errors.SpecialistPhone}
                  />
                </div>
                <div className="border rounded p-2 mb-4">
                  <Select
                    name="SelectedManager"
                    label="Manager"
                    value={data.SelectedManager}
                    options={stateEmployeeOptions}
                    onChange={handleSelectedManagerChange}
                    error={errors.Manager}
                  />
                  <TrueInput
                    name="ManagerPhone"
                    label="Phone"
                    value={data.ManagerPhone}
                    onChange={(e) => {
                      if (!/^\d+$/.test(e.currentTarget.value)) {
                        const errs = { ...errors };
                        errs.ManagerPhone = "Numbers only, no dashes";
                        setErrors(errs);
                        return;
                      }

                      if (e.currentTarget.value.length !== 10) {
                        const errs = { ...errors };
                        errs.ManagerPhone = "Phone number must be 10 digits";
                        setErrors(errs);
                      }

                      if (errors.ManagerPhone !== undefined) {
                        const errs = { ...errors };
                        delete errs.ManagerPhone;
                        setErrors(errs);
                      }

                      const obj = { ...data };
                      obj.ManagerPhone = e.currentTarget.value;
                      setData(obj);
                    }}
                    error={errors.ManagerPhone}
                  />
                </div>
                {data.SelectedCompany?.includes("Kentucky") ||
                (moment().isAfter(moment("2025-04-04")) &&
                  data.SelectedCompany?.includes("Alabama")) ||
                data.SelectedDivision === "America" ? (
                  <div className="border rounded p-2">
                    <Select
                      name="InPerson"
                      label="In Person"
                      value={data.InPerson[0].ReceiverId}
                      options={
                        data.OutOfRegion
                          ? stateEmployeeOptions
                          : employeeOptions
                      }
                      onChange={handleSelectedInPersonChange}
                      error={errors.SelectedInPerson}
                    />
                    <TrueInput
                      name="InPersonPhone"
                      label="Phone"
                      value={data.InPerson[0].ReceiverPhone}
                      onChange={(e) => {
                        if (!/^\d+$/.test(e.currentTarget.value)) {
                          const errs = { ...errors };
                          errs.InPerson = "Numbers only, no dashes";
                          setErrors(errs);
                          return;
                        }

                        if (e.currentTarget.value.length !== 10) {
                          const errs = { ...errors };
                          errs.InPerson = "Phone number must be 10 digits";
                          setErrors(errs);
                        }

                        if (errors.InPerson !== undefined) {
                          const errs = { ...errors };
                          delete errs.InPerson;
                          setErrors(errs);
                        }

                        const obj = { ...data };
                        obj.InPerson[0].ReceiverPhone = e.currentTarget.value;
                        setData(obj);
                      }}
                    />
                    {errors.InPerson && (
                      <div className="alert alert-danger">
                        {errors.InPerson}
                      </div>
                    )}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="row">
                  <div className="col">
                    <div className="border rounded p-2 mb-4">
                      <Select
                        name="SelectedSpecialist"
                        label="Specialist"
                        value={data.SelectedSpecialist}
                        options={employeeOptions}
                        onChange={handleSelectedSpecialistChange}
                        error={errors.SelectedSpecialist}
                      />
                      <TrueInput
                        name="SpecialistPhone"
                        label="Phone"
                        value={data.SpecialistPhone}
                        onChange={(e) => {
                          if (!/^\d+$/.test(e.currentTarget.value)) {
                            const errs = { ...errors };
                            errs.SpecialistPhone = "Numbers only, no dashes";
                            setErrors(errs);
                            return;
                          }

                          if (e.currentTarget.value.length !== 10) {
                            const errs = { ...errors };
                            errs.SpecialistPhone =
                              "Phone number must be 10 digits";
                            setErrors(errs);
                          }

                          if (errors.SpecialistPhone !== undefined) {
                            const errs = { ...errors };
                            delete errs.SpecialistPhone;
                            setErrors(errs);
                          }

                          const obj = { ...data };
                          obj.SpecialistPhone = e.currentTarget.value;
                          setData(obj);
                        }}
                        error={errors.SpecialistPhone}
                      />
                    </div>
                  </div>
                  <div className="col">
                    <div className="border rounded p-2 mb-4">
                      <Select
                        name="SelectedManager"
                        label="Manager"
                        value={data.SelectedManager}
                        options={stateEmployeeOptions}
                        onChange={handleSelectedManagerChange}
                        error={errors.Manager}
                      />
                      <TrueInput
                        name="ManagerPhone"
                        label="Phone"
                        value={data.ManagerPhone}
                        onChange={(e) => {
                          if (!/^\d+$/.test(e.currentTarget.value)) {
                            const errs = { ...errors };
                            errs.ManagerPhone = "Numbers only, no dashes";
                            setErrors(errs);
                            return;
                          }

                          if (e.currentTarget.value.length !== 10) {
                            const errs = { ...errors };
                            errs.ManagerPhone =
                              "Phone number must be 10 digits";
                            setErrors(errs);
                          }

                          if (errors.ManagerPhone !== undefined) {
                            const errs = { ...errors };
                            delete errs.ManagerPhone;
                            setErrors(errs);
                          }

                          const obj = { ...data };
                          obj.ManagerPhone = e.currentTarget.value;
                          setData(obj);
                        }}
                        error={errors.ManagerPhone}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2">In Person</div>
                <div className="container border rounded">
                  <div className="row border-bottom py-2">
                    <div className="col">Division</div>
                    <div className="col">Responder</div>
                    <div className="col">Phone</div>
                  </div>
                  <div className="row border-bottom py-2">
                    <div className="col d-flex align-items-center">
                      Fort Wayne
                    </div>
                    <div className="col d-flex align-items-center">
                      <Select
                        name="InPerson"
                        value={data.InPerson[0]?.ReceiverId}
                        options={employeeOptions}
                        onChange={(e) => handleSelectedInPersonChange(e, 0)}
                        divClassName="flex-fill"
                        customMarginBottom="mb-0"
                      />
                    </div>
                    <div className="col d-flex align-items-center">
                      <TrueInput
                        name="InPersonPhone"
                        value={data.InPerson[0]?.ReceiverPhone}
                        onChange={(e) => handleInPersonPhoneChange(e, 0)}
                        divClassName="flex-fill"
                      />
                    </div>
                  </div>
                  <div className="row border-bottom py-2">
                    <div className="col d-flex align-items-center">
                      Indianapolis
                    </div>
                    <div className="col d-flex align-items-center">
                      <Select
                        name="InPerson"
                        value={data.InPerson[1]?.ReceiverId}
                        options={employeeOptions}
                        onChange={(e) => handleSelectedInPersonChange(e, 1)}
                        divClassName="flex-fill"
                        customMarginBottom="mb-0"
                      />
                    </div>
                    <div className="col d-flex align-items-center">
                      <TrueInput
                        name="InPersonPhone"
                        value={data.InPerson[1]?.ReceiverPhone}
                        onChange={(e) => handleInPersonPhoneChange(e, 1)}
                        divClassName="flex-fill"
                      />
                    </div>
                  </div>
                  <div className="row border-bottom py-2">
                    <div className="col d-flex align-items-center">
                      Merrillville
                    </div>
                    <div className="col d-flex align-items-center">
                      <Select
                        name="InPerson"
                        value={data.InPerson[2]?.ReceiverId}
                        options={employeeOptions}
                        onChange={(e) => handleSelectedInPersonChange(e, 2)}
                        divClassName="flex-fill"
                        customMarginBottom="mb-0"
                      />
                    </div>
                    <div className="col d-flex align-items-center">
                      <TrueInput
                        name="InPersonPhone"
                        value={data.InPerson[2]?.ReceiverPhone}
                        onChange={(e) => handleInPersonPhoneChange(e, 2)}
                        divClassName="flex-fill"
                      />
                    </div>
                  </div>
                  <div className="row border-bottom py-2">
                    <div className="col d-flex align-items-center">
                      Mishawaka
                    </div>
                    <div className="col d-flex align-items-center">
                      <Select
                        name="InPerson"
                        value={data.InPerson[3]?.ReceiverId}
                        options={employeeOptions}
                        onChange={(e) => handleSelectedInPersonChange(e, 3)}
                        divClassName="flex-fill"
                        customMarginBottom="mb-0"
                      />
                    </div>
                    <div className="col d-flex align-items-center">
                      <TrueInput
                        name="InPersonPhone"
                        value={data.InPerson[3]?.ReceiverPhone}
                        onChange={(e) => handleInPersonPhoneChange(e, 3)}
                        divClassName="flex-fill"
                      />
                    </div>
                  </div>
                  <div className="row py-2">
                    <div className="col d-flex align-items-center">
                      Southern Indiana
                    </div>
                    <div className="col d-flex align-items-center">
                      <Select
                        name="InPerson"
                        value={data.InPerson[4]?.ReceiverId}
                        options={employeeOptions}
                        onChange={(e) => handleSelectedInPersonChange(e, 4)}
                        divClassName="flex-fill"
                        customMarginBottom="mb-0"
                      />
                    </div>
                    <div className="col d-flex align-items-center">
                      <TrueInput
                        name="InPersonPhone"
                        value={data.InPerson[4]?.ReceiverPhone}
                        onChange={(e) => handleInPersonPhoneChange(e, 4)}
                        divClassName="flex-fill"
                      />
                    </div>
                  </div>
                  {errors.InPerson && (
                    <div className="alert alert-danger">{errors.InPerson}</div>
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className="mt-2">
              <button className="btn btn-danger" onClick={handleHideModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary mx-2"
                onClick={handleSave}
                disabled={Object.keys(errors).length > 0}
              >
                Save
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default OnCall;
